import {
  ConstellationOneListing,
  LISTINGS_API,
  MLSOUID,
  ToolkitListingID,
  isMLSOUID,
  isToolkitListingID,
  isUndefined,
  mlsOUIDByMLSID,
  parseToolkitListingID,
} from '@aloft/toolkit-shared';
import { logger } from '@sentry/react';

import { fetchGetListings } from '../../../hooks/api/toolkitComponents.js';
import { ToolkitFetcherConfig } from '../../../hooks/api/toolkitContext.js';
import { Batcher } from '../../../utils/batcher/index.js';
import { ListingResult } from './index.js';

export function getMlsListingsBatcher(): Batcher.Batcher<
  ListingResult,
  ToolkitListingID,
  ToolkitFetcherConfig
> {
  return Batcher.create<ListingResult, ToolkitListingID, ToolkitFetcherConfig>({
    fetcher: async (ids: ToolkitListingID[], fetcherOptions?: ToolkitFetcherConfig) => {
      if (isUndefined(fetcherOptions)) {
        throw Error('Must provide fetcher options');
      }
      const batches: { [key in MLSOUID]?: string[] } = ids.reduce(
        (acc: { [key in MLSOUID]?: string[] }, id: ToolkitListingID) => {
          const { mls, listingID } = parseToolkitListingID(id);
          const mlsOUID = isMLSOUID(mls) ? mls : mlsOUIDByMLSID(mls);
          if (isUndefined(mlsOUID)) {
            logger.warn(
              `getMlsListingsBatcher: Invalid MLSOUID for listing ID ${listingID} with MLS ${mls}`,
            );
            return acc; // Skip if mlsOUID is undefined
          }
          const existingListingIDs = acc[mlsOUID] ?? [];
          if (!existingListingIDs.includes(listingID)) {
            acc[mlsOUID] = existingListingIDs.concat(listingID);
          }
          return acc;
        },
        {},
      );
      const responses = Object.entries(batches).map(async ([mlsOUID, listingIDs]) => {
        const response = await fetchGetListings({
          ...fetcherOptions,
          body: LISTINGS_API.POST.REQUEST.BODY.parse({
            mlsOUID,
            listingIDs,
          }),
        });
        return (response.results as ConstellationOneListing[]) ?? [];
      });
      return Promise.all(responses).then((results) =>
        results.flat().map((listing) => {
          return { status: 'found', listing };
        }),
      );
    },
    resolver: (items, toolkitListingID) => {
      if (!isToolkitListingID(toolkitListingID)) {
        return { status: 'missing' };
      }
      const { listingID } = parseToolkitListingID(toolkitListingID);
      return (
        items.find(
          (item) =>
            item.status === 'found' &&
            item.listing.ToolkitListingID &&
            parseToolkitListingID(item.listing.ToolkitListingID).listingID === listingID,
        ) ?? { status: 'missing' }
      );
    },
    scheduler: Batcher.windowScheduler(10), // 10 ms
  });
}
